import { FC, useState } from 'react'
import { Adjuster, Props as AdjusterProps } from './Adjuster'
import { Button } from 'components/buttons/Button'
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPenToSquare } from '@fortawesome/pro-regular-svg-icons'
import { DateTime } from 'luxon'
import { getTimeZoneAbbreviation } from '@utils/getTimeZoneAbbreviation'
import {
  getClockFromElapsedSecondsFromMidnight,
  hoursMinutesToSeconds,
} from '@utils/time'
import { Time } from '../types'
import {
  computeDuration,
  computeTime,
  convertFromClockToElapsedTimeSinceMidnight,
  convertSecondsFromMidnightIntoTime,
  isTimeDefined,
  nullTime,
  validate,
} from '../utils'
import { useLanguage } from '@hooks/useLanguage'
import { Title } from './Title'

const ClearButton: typeof Button = ({ onClick }) => {
  const { t } = useTranslation()

  return (
    <button
      type="button"
      className="text-sm font-medium text-neutral-400"
      onClick={onClick}
    >
      {t('components.timeWidget.clearTime')}
    </button>
  )
}

const SubmitButton: typeof Button = ({ disabled }) => {
  const { t } = useTranslation()

  return (
    <Button
      disabled={disabled}
      className="min-w-[6rem] text-sm"
      icon={faChevronRight}
      tabIndex={4}
    >
      {t('components.timeWidget.apply')}
    </Button>
  )
}

type TimeAdjusterProps = {
  autoFocusedAdjuster: 'start' | 'end'
  dateEditing: DateTime
  displayMode: '12' | '24'
  endTimeSinceMidnight: number | null
  fieldPrefix?: string
  onSubmit: (
    startAtSecondsSinceMidnight: number | null,
    endAtSecondsSinceMidnight: number | null,
    timeZone: string,
  ) => void
  renderTopRight?: () => React.ReactNode
  startTimeSinceMidnight: number | null
  timeZone: string
  title?: string
} & (
  | {
      hideTimeZoneEditButton?: boolean
      variant: 'time'
      onTimeZoneEditClick: () => void
    }
  | {
      variant: 'time-and-duration'
    }
)

export const TimeAdjuster: FC<TimeAdjusterProps> = ({
  autoFocusedAdjuster,
  dateEditing,
  displayMode,
  endTimeSinceMidnight: endTimeSinceMidnightInitial,
  fieldPrefix,
  onSubmit,
  renderTopRight = () => null,
  startTimeSinceMidnight: startTimeSinceMidnightInitial,
  timeZone,
  title,
  variant,
  ...props
}) => {
  const locale = useLanguage()
  const [startTimeSinceMidnight, setStartTimeSinceMidnight] = useState<Time>(
    convertSecondsFromMidnightIntoTime(startTimeSinceMidnightInitial),
  )
  const [endTimeSinceMidnight, setEndTimeSinceMidnight] = useState<Time>(
    convertSecondsFromMidnightIntoTime(endTimeSinceMidnightInitial),
  )
  const [duration, setDuration] = useState<Time>(
    computeDuration(startTimeSinceMidnight, endTimeSinceMidnight),
  )

  const { errors, isInvalid } = validate(
    startTimeSinceMidnight,
    endTimeSinceMidnight,
    duration,
  )

  const handleSubmit = () => {
    if (isInvalid) return

    const startAtSecondsSinceMidnight = hoursMinutesToSeconds(
      startTimeSinceMidnight.hour,
      startTimeSinceMidnight.minute,
    )
    const endAtSecondsSinceMidnight = hoursMinutesToSeconds(
      endTimeSinceMidnight.hour,
      endTimeSinceMidnight.minute,
    )

    onSubmit(startAtSecondsSinceMidnight, endAtSecondsSinceMidnight, timeZone)
  }

  const handleClear = () => {
    setStartTimeSinceMidnight(nullTime)
    setEndTimeSinceMidnight(nullTime)
    setDuration(nullTime)
  }

  const handleStartTimeChange: AdjusterProps['onChange'] = (
    clockStartHour,
    clockStartMinute,
  ) => {
    const startTimeSinceMidnight = convertFromClockToElapsedTimeSinceMidnight(
      clockStartHour,
      clockStartMinute,
      dateEditing,
      timeZone,
      { assumeMidnightIsEndOfDay: false },
    )
    setStartTimeSinceMidnight(startTimeSinceMidnight)

    if (!isTimeDefined(startTimeSinceMidnight)) return

    if (isTimeDefined(duration) && !isTimeDefined(endTimeSinceMidnight)) {
      const newEndTime = computeTime(startTimeSinceMidnight, duration, 'end')
      setEndTimeSinceMidnight(newEndTime)
      return
    }

    setDuration(computeDuration(startTimeSinceMidnight, endTimeSinceMidnight))
  }

  const handleEndTimeChange: AdjusterProps['onChange'] = (
    clockEndHour,
    clockEndMinute,
  ) => {
    const endTimeSinceMidnight = convertFromClockToElapsedTimeSinceMidnight(
      clockEndHour,
      clockEndMinute,
      dateEditing,
      timeZone,
      { assumeMidnightIsEndOfDay: true },
    )
    setEndTimeSinceMidnight(endTimeSinceMidnight)

    if (!isTimeDefined(endTimeSinceMidnight)) return

    if (isTimeDefined(duration) && !isTimeDefined(startTimeSinceMidnight)) {
      const newStartTime = computeTime(endTimeSinceMidnight, duration, 'start')
      setStartTimeSinceMidnight(newStartTime)
      return
    }

    setDuration(computeDuration(startTimeSinceMidnight, endTimeSinceMidnight))
  }

  const handleDurationChange: AdjusterProps['onChange'] = (
    durationHour,
    durationMinute,
  ) => {
    const changedDuration = { hour: durationHour, minute: durationMinute }
    setDuration(changedDuration)

    if (!isTimeDefined(changedDuration)) return

    if (
      isTimeDefined(changedDuration) &&
      isTimeDefined(startTimeSinceMidnight)
    ) {
      const newEndTime = computeTime(
        startTimeSinceMidnight,
        changedDuration,
        'end',
      )
      setEndTimeSinceMidnight(newEndTime)
      return
    }

    if (isTimeDefined(changedDuration) && isTimeDefined(endTimeSinceMidnight)) {
      const newStartTime = computeTime(
        endTimeSinceMidnight,
        changedDuration,
        'start',
      )
      setStartTimeSinceMidnight(newStartTime)
    }
  }

  const startClock = isTimeDefined(startTimeSinceMidnight)
    ? getClockFromElapsedSecondsFromMidnight(
        startTimeSinceMidnight.hour * 60 ** 2 +
          startTimeSinceMidnight.minute * 60,
        dateEditing,
        timeZone,
      )
    : { hour: null, minute: null }

  const endClock = isTimeDefined(endTimeSinceMidnight)
    ? getClockFromElapsedSecondsFromMidnight(
        endTimeSinceMidnight.hour * 60 ** 2 + endTimeSinceMidnight.minute * 60,
        dateEditing,
        timeZone,
      )
    : { hour: null, minute: null }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        handleSubmit()
      }}
    >
      <Title {...(title ? { title } : { date: dateEditing })}>
        {renderTopRight()}
        {'hideTimeZoneEditButton' in props &&
          !props.hideTimeZoneEditButton &&
          'onTimeZoneEditClick' in props &&
          variant === 'time' && (
            <button
              className="flex flex-row items-center gap-1 text-xs"
              type="button"
              onClick={(event) => {
                event.preventDefault()
                props.onTimeZoneEditClick()
              }}
            >
              {getTimeZoneAbbreviation(timeZone, dateEditing, locale)}
              <FontAwesomeIcon
                className="text-neutral-500"
                icon={faPenToSquare}
              />
            </button>
          )}
      </Title>
      <div className="flex flex-row p-4 border-b-2 border-neutral-200 space-x-4">
        <Adjuster
          adjusting="start"
          displayMode={displayMode}
          fieldPrefix={fieldPrefix}
          errors={errors.start}
          clockHour={startClock.hour}
          clockMinute={startClock.minute}
          onChange={handleStartTimeChange}
          autoFocus={autoFocusedAdjuster === 'start'}
          tabIndex={1}
        />
        <Adjuster
          assumePm={displayMode === '12'}
          adjusting="end"
          displayMode={displayMode}
          fieldPrefix={fieldPrefix}
          errors={errors.end}
          clockHour={endClock.hour}
          clockMinute={endClock.minute}
          onChange={handleEndTimeChange}
          autoFocus={autoFocusedAdjuster === 'end'}
          tabIndex={2}
        />
        <Adjuster
          adjusting="duration"
          defaultDialHour={1}
          displayMode="24"
          fieldPrefix={fieldPrefix}
          errors={errors.duration}
          hidden={variant === 'time'}
          clockHour={duration.hour}
          clockMinute={duration.minute}
          onChange={handleDurationChange}
          tabIndex={3}
        />
      </div>
      <div className="flex flex-row justify-between px-4 pt-2">
        <ClearButton onClick={handleClear} />
        <SubmitButton disabled={isInvalid} />
      </div>
    </form>
  )
}
